import * as React from 'react';
import { useSelector } from 'react-redux';
import { Responsive, useDeviceType } from '@cian/ui-kit';

import { useRequestButtonClickHandler, useStandardButtonSize } from './hooks';
import {
  Advantages,
  FindApartment,
  Header,
  LandingPageLayout,
  PlusMinus,
  StickButtons,
} from '../../components/LandingPage';
import { RequestExistsModalWindow } from '../../components/RequestExistsModalWindow';
import { selectConsultantPhone } from '../../selectors/consultantContacts';
import { selectShowRequestForm } from '../../selectors/requestForm';
import { trackKpnCallClick } from '../../utils/analytics';
import { CallButtonContainer } from '../CallButton';
import { ContactsModalWindowContainer } from '../ContactsModalWindow';
import { RequestButtonContainer } from '../RequestButton';
import { RequestFormModalWindowContainer } from '../RequestFormModalWindow';

export const LandingPageContainer: React.FC = () => {
  const [requestModalOpen, setRequestModalOpen] = React.useState<boolean>(false);
  const [contactsModalOpen, setContactsModalOpen] = React.useState(false);
  const [requestExistsModalOpen, setRequestExistsModalOpen] = React.useState(false);
  const showRequestForm = useSelector(selectShowRequestForm);
  const phone = useSelector(selectConsultantPhone);

  const deviceType = useDeviceType();

  const handleCallButtonClick = () => {
    if (deviceType === 'desktop') {
      setContactsModalOpen(true);
    } else {
      window.location.assign(`tel:${phone}`);
    }

    trackKpnCallClick();
  };

  const handleRequestButtonClick = useRequestButtonClickHandler({
    openRequestFormModal: () => setRequestModalOpen(true),
    openRequestExistsModal: () => setRequestExistsModalOpen(true),
  });

  const closeRequestModal = React.useCallback(() => setRequestModalOpen(false), []);
  const closeContactsModal = React.useCallback(() => setContactsModalOpen(false), []);
  const closeRequestExistsModal = React.useCallback(() => setRequestExistsModalOpen(false), []);

  React.useEffect(() => {
    setRequestModalOpen(showRequestForm);
  }, [showRequestForm]);

  const headerButtonsRef = React.useRef<HTMLDivElement>(null);
  const footerButtonsRef = React.useRef<HTMLDivElement>(null);

  const standardButtonSize = useStandardButtonSize();

  return (
    <>
      <LandingPageLayout>
        <Header
          buttonsRef={headerButtonsRef}
          callButton={
            <CallButtonContainer
              size={standardButtonSize}
              data-testid="ContactsHeaderButton"
              onClick={handleCallButtonClick}
            />
          }
          requestButton={
            <RequestButtonContainer
              size={standardButtonSize}
              data-testid="RequestFormButton"
              onClick={handleRequestButtonClick}
            />
          }
        />
        <PlusMinus />
        <Advantages />
        <FindApartment
          buttonsRef={footerButtonsRef}
          callButton={<CallButtonContainer size={standardButtonSize} onClick={handleCallButtonClick} />}
          requestButton={<RequestButtonContainer size={standardButtonSize} onClick={handleRequestButtonClick} />}
        />
        <Responsive phone>
          <StickButtons
            headerButtonsRef={headerButtonsRef}
            footerButtonsRef={footerButtonsRef}
            callButton={
              <CallButtonContainer
                size="XS"
                text="Позвонить"
                data-testid="ContactsStickButton"
                onClick={handleCallButtonClick}
              />
            }
            requestButton={
              <RequestButtonContainer
                size="XS"
                theme="fill_secondary"
                data-testid="RequestFormStickButton"
                onClick={handleRequestButtonClick}
              />
            }
          />
        </Responsive>
      </LandingPageLayout>
      <RequestFormModalWindowContainer open={requestModalOpen} onClose={closeRequestModal} />
      <ContactsModalWindowContainer open={contactsModalOpen} onClose={closeContactsModal} />
      <RequestExistsModalWindow open={requestExistsModalOpen} onClose={closeRequestExistsModal} />
    </>
  );
};
